import { useContext, useEffect, useRef, useState } from "react";
import { FaWhatsapp } from "react-icons/fa";
import { IoClose, IoArrowForward } from "react-icons/io5";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { DarkModeContext } from "../Context/DarkModeContext";
import { useLocation } from "react-router-dom";

const whatsappUrl = process.env.REACT_APP_WHATSAPP_URL;

export default function StudentGlobalActions() {
  const { isDarkMode } = useContext(DarkModeContext);
  const [modalOpen, setModalOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [footerOffset, setFooterOffset] = useState(0);
  const { pathname } = useLocation();
  const widgetRef = useRef(null);
  const triggerRef = useRef(null);
  const closeRef = useRef(null);
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    const footer = document.querySelector(".home-footer");
    if (!footer) {
      setFooterOffset(0);
      return;
    }
    let frame;
    const update = () => {
      setFooterOffset(
        Math.max(0, window.innerHeight - footer.getBoundingClientRect().top),
      );
    };
    const scheduleUpdate = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", scheduleUpdate, {
      passive: true,
      capture: true,
    });
    window.addEventListener("resize", scheduleUpdate);
    const observer = new ResizeObserver(scheduleUpdate);
    observer.observe(footer);
    if (footer.parentElement) observer.observe(footer.parentElement);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("scroll", scheduleUpdate, true);
      window.removeEventListener("resize", scheduleUpdate);
      observer.disconnect();
    };
  }, [pathname]);

  useEffect(() => {
    const updateVisibility = () => {
      const overlays = document.querySelectorAll(
        '[role="dialog"], .fixed.inset-0, .fixed.top-0.left-0.right-0.bottom-0',
      );
      const visible = Array.from(overlays).some((element) => {
        const style = window.getComputedStyle(element);
        return style.display !== "none" && style.visibility !== "hidden";
      });
      setModalOpen(visible);
      if (visible) setOpen(false);
    };
    updateVisibility();
    const observer = new MutationObserver(updateVisibility);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!open) return;
    closeRef.current?.focus({ preventScroll: true });
    const outside = (event) => {
      if (!widgetRef.current?.contains(event.target)) setOpen(false);
    };
    const escape = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus({ preventScroll: true });
      }
    };
    document.addEventListener("pointerdown", outside);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("pointerdown", outside);
      document.removeEventListener("keydown", escape);
    };
  }, [open]);

  if (modalOpen || !whatsappUrl) return null;

  const close = () => {
    setOpen(false);
    triggerRef.current?.focus({ preventScroll: true });
  };

  return (
    <div
      ref={widgetRef}
      className="fixed right-4 z-30 flex flex-col items-end sm:right-6"
      style={{
        bottom: `calc(1rem + env(safe-area-inset-bottom, 0px) + ${footerOffset}px)`,
      }}
    >
      <AnimatePresence>
        {open && (
          <motion.section
            id="whatsapp-support"
            aria-labelledby="whatsapp-support-title"
            initial={{
              opacity: 0,
              y: reduceMotion ? 0 : 18,
              scale: reduceMotion ? 1 : 0.92,
            }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{
              opacity: 0,
              y: reduceMotion ? 0 : 12,
              scale: reduceMotion ? 1 : 0.94,
            }}
            transition={{
              duration: reduceMotion ? 0 : 0.22,
              ease: [0.22, 1, 0.36, 1],
            }}
            className={`mb-4 w-[340px] max-w-[calc(100vw-2rem)] origin-bottom-right overflow-hidden rounded-2xl border shadow-[0_16px_60px_rgba(0,0,0,0.22)] ${isDarkMode ? "border-white/10 bg-[#142b32] text-white" : "border-black/5 bg-white text-slate-800"}`}
            style={{
              maxHeight: `calc(100dvh - 7rem - env(safe-area-inset-bottom, 0px) - ${footerOffset}px)`,
              overflowY: "auto",
            }}
          >
            <div className="flex items-center gap-3 bg-[#075E54] p-5 text-white">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white/15">
                <FaWhatsapp size={27} aria-hidden="true" />
              </span>
              <div className="flex-1">
                <h2 id="whatsapp-support-title" className="text-base font-bold">
                  EduHigh
                </h2>
                <p className="mt-0.5 text-xs text-white/80">
                  Here to help you learn
                </p>
              </div>
              <button
                ref={closeRef}
                type="button"
                onClick={close}
                aria-label="Close WhatsApp support"
                className="flex h-9 w-9 items-center justify-center rounded-full hover:bg-white/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
              >
                <IoClose size={22} aria-hidden="true" />
              </button>
            </div>
            <div
              className={`p-5 ${isDarkMode ? "bg-white/[0.03]" : "bg-[#f0f5f1]"}`}
            >
              <div
                className={`rounded-2xl rounded-tl-none p-4 shadow-sm ${isDarkMode ? "bg-[#203c42]" : "bg-white"}`}
              >
                <p
                  className={`mb-1 text-xs font-semibold ${isDarkMode ? "text-emerald-300" : "text-[#075E54]"}`}
                >
                  EduHigh
                </p>
                <p className="text-sm font-semibold">
                  Hi there! <span aria-hidden="true">&#128075;</span>
                </p>
                <p className="mt-1 text-sm leading-relaxed">
                  Have a question about your courses or account? Chat with us on
                  WhatsApp.
                </p>
              </div>
            </div>
            <div className="p-4">
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex min-h-[48px] items-center justify-center gap-2 rounded-full bg-[#25D366] px-5 py-3 text-sm font-bold text-[#063c25] transition-colors hover:bg-[#20c35d] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 focus-visible:ring-offset-2"
                aria-label="Start WhatsApp chat (opens in a new tab)"
              >
                <FaWhatsapp size={21} aria-hidden="true" />
                Start chat
                <IoArrowForward size={18} aria-hidden="true" />
              </a>
              <p
                className={`mt-2 text-center text-[11px] ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}
              >
                Opens in WhatsApp
              </p>
            </div>
          </motion.section>
        )}
      </AnimatePresence>
      <motion.button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-label={open ? "Close WhatsApp support" : "Open WhatsApp support"}
        aria-expanded={open}
        aria-controls={open ? "whatsapp-support" : undefined}
        whileHover={reduceMotion ? undefined : { scale: 1.06 }}
        whileTap={reduceMotion ? undefined : { scale: 0.94 }}
        className="relative flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366] text-white shadow-[0_6px_24px_rgba(7,94,84,0.3)] ring-1 ring-black/5 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-emerald-300 focus-visible:ring-offset-2 sm:h-16 sm:w-16"
      >
        <motion.span
          animate={{
            rotate: open && !reduceMotion ? 90 : 0,
            scale: open ? 0 : 1,
            opacity: open ? 0 : 1,
          }}
          transition={{ duration: reduceMotion ? 0 : 0.18 }}
          className="absolute flex"
          aria-hidden="true"
        >
          <FaWhatsapp size={32} />
        </motion.span>
        <motion.span
          animate={{
            rotate: open || reduceMotion ? 0 : -90,
            scale: open ? 1 : 0,
            opacity: open ? 1 : 0,
          }}
          transition={{ duration: reduceMotion ? 0 : 0.18 }}
          className="absolute flex"
          aria-hidden="true"
        >
          <IoClose size={30} />
        </motion.span>
      </motion.button>
    </div>
  );
}
